import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Inbox } from "lucide-react";
import { communitiesAPI, connectionsAPI } from "../services/api";
import { useAuthState } from "../hooks/useAuth";
import {
  buildInboxNavigationState,
  clearInboxRouteCache,
  writeInboxRouteCache,
} from "../utils/inboxRouteCache";
import "./InboxButton.css";

let cachedUnreadCount = 0;
let cachedUnreadCountAt = 0;
let pendingUnreadCountRequest = null;
let pendingInboxPrefetchRequest = null;

const INBOX_COUNT_CACHE_MS = 15000;

const InboxButton = ({
  variant = "navigation",
  disabled = false,
  className = "",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, loading } = useAuthState();
  const [pendingCount, setPendingCount] = useState(0);
  const isInbox = location.pathname === "/inbox";
  const isDisabled = disabled || isInbox;

  const prefetchInbox = () => {
    if (loading || !token) return Promise.resolve(null);

    const cachedState = buildInboxNavigationState();
    if (cachedState.prefetchedInbox) return Promise.resolve(cachedState);
    if (pendingInboxPrefetchRequest) return pendingInboxPrefetchRequest;

    pendingInboxPrefetchRequest = Promise.all([
      communitiesAPI.getInbox(),
      connectionsAPI.getConnections("inbox"),
      connectionsAPI.getInboxConnections(),
    ])
      .then(([response, connectionsResponse, connectionInboxResponse]) => {
        const connectionItems = (connectionsResponse.data?.connections || []).map(
          (connection) => ({
            ...connection,
            itemKind: "connection",
            inboxStatus: connection.inboxStatus || "read",
          }),
        );
        const connectionNotificationItems = (
          connectionInboxResponse.data?.notifications || []
        ).map((notification) => ({
          ...notification,
          itemKind: "connectionNotification",
          inboxStatus: notification.inboxStatus || "read",
        }));
        const snapshot = {
          inbox: {
            unread: [
              ...connectionItems.filter((connection) => connection.isUnread),
              ...connectionNotificationItems.filter(
                (notification) => notification.inboxStatus === "unread",
              ),
              ...(response.data?.unread || []),
            ],
            read: [
              ...connectionItems.filter((connection) => !connection.isUnread),
              ...connectionNotificationItems.filter(
                (notification) => notification.inboxStatus !== "unread",
              ),
              ...(response.data?.read || []),
            ],
          },
          sectionLimit: response.data?.sectionLimit || 25,
          historyLimit: response.data?.historyLimit || 120,
        };
        writeInboxRouteCache(snapshot);
        return { prefetchedInbox: snapshot };
      })
      .catch(() => null)
      .finally(() => {
        pendingInboxPrefetchRequest = null;
      });

    return pendingInboxPrefetchRequest;
  };

  useEffect(() => {
    if (loading || !token) {
      setPendingCount(0);
      return;
    }

    let isMounted = true;

    const loadInboxCount = async ({ force = false } = {}) => {
      const now = Date.now();

      if (!force && now - cachedUnreadCountAt < INBOX_COUNT_CACHE_MS) {
        setPendingCount(cachedUnreadCount);
        return;
      }

      try {
        if (!pendingUnreadCountRequest) {
          pendingUnreadCountRequest = Promise.all([
            communitiesAPI.getInboxCount(),
            connectionsAPI.getInboxConnections(),
          ])
            .finally(() => {
              pendingUnreadCountRequest = null;
            });
        }

        const [inboxResponse, connectionsResponse] =
          await pendingUnreadCountRequest;
        if (!isMounted) return;

        cachedUnreadCount =
          (inboxResponse.data?.unreadCount || 0) +
          (connectionsResponse.data?.unreadCount || 0);
        cachedUnreadCountAt = Date.now();
        setPendingCount(cachedUnreadCount);
      } catch {
        if (isMounted) {
          setPendingCount(0);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadInboxCount();
      }
    };

    const handleInboxChanged = (event) => {
      if (typeof event.detail?.unreadCount === "number") {
        cachedUnreadCount = Math.max(0, event.detail.unreadCount);
        cachedUnreadCountAt = Date.now();
        setPendingCount(cachedUnreadCount);
        return;
      }

      clearInboxRouteCache();
      loadInboxCount({ force: true });
    };

    loadInboxCount();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("invites-changed", handleInboxChanged);
    window.addEventListener("inbox-changed", handleInboxChanged);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("invites-changed", handleInboxChanged);
      window.removeEventListener("inbox-changed", handleInboxChanged);
    };
  }, [loading, token]);

  if (loading || !token) {
    return null;
  }

  const handleOpenInbox = async () => {
    if (isDisabled) return;

    const cachedState = buildInboxNavigationState();
    if (cachedState.prefetchedInbox) {
      navigate("/inbox", { state: cachedState });
      return;
    }

    const prefetchedState = await prefetchInbox();
    navigate("/inbox", {
      state: prefetchedState?.prefetchedInbox
        ? prefetchedState
        : buildInboxNavigationState(),
    });
  };

  return (
    <button
      className={`inbox-button inbox-button--${variant} ${className}`}
      onClick={handleOpenInbox}
      onFocus={prefetchInbox}
      onMouseEnter={prefetchInbox}
      onTouchStart={prefetchInbox}
      title="Inbox"
      aria-label={
        pendingCount > 0 ? `Inbox, ${pendingCount} unread` : "Inbox"
      }
      type="button"
      disabled={isDisabled}
    >
      <Inbox size={18} strokeWidth={2} />
      {pendingCount > 0 ? (
        <span className="inbox-button-badge">{pendingCount}</span>
      ) : null}
    </button>
  );
};

export default InboxButton;
